// services/studentService.js - Student profile management
import { get, put } from '@aws-amplify/api-rest';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_NAME = 'CourseBookingApi';

// Helper to get auth headers
async function getAuthHeaders() {
  try {
    const session = await fetchAuthSession();
    if (!session || !session.tokens || !session.tokens.idToken) {
      console.error('Authentication Failure: No valid session or token found.');
      throw new Error('No authentication token');
    }
    const headers = {
      Authorization: `Bearer ${session.tokens.idToken.toString()}`
    };
    //console.log('Authorization Headers:', headers);
    return headers;
  } catch (error) {
    console.error('Error fetching auth session:', error);
    throw error;
  }
}

// Enhanced error handling wrapper (same pattern as courseService)
const handleApiCall = async (apiCall) => {
  try {
    let response = await apiCall();

    // UNWRAP {response: Promise, ...}
    if (response && response.response && typeof response.response.then === 'function') {
      response = await response.response;
    }

    // Parse fetch Response
    if (response && typeof response.json === 'function') {
      response = await response.json();
    }

    // Handle ReadableStream (Amplify/fetch)
    if (response && response.body && typeof response.body.getReader === 'function') {
      const reader = response.body.getReader();
      let chunks = [];
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value) chunks.push(...value);
      }
      const text = new TextDecoder().decode(new Uint8Array(chunks));
      try {
        response = { ...response, ...JSON.parse(text) };
      } catch (e) {
        // Not JSON, ignore
      }
    }

    // If .body is a string, parse it and merge (the classic Lambda proxy pattern)
    if (response && typeof response.body === 'string') {
      try {
        response = { ...response, ...JSON.parse(response.body) };
      } catch (e) {
        // Not JSON, ignore
      }
    }

    return response;
  } catch (error) {
    console.error(`API Error in ${apiCall.name}:`, error);

    // Check if it's an auth error
    if (error.statusCode === 401 || error.name === 'NotAuthorizedException') {
      console.error('Authentication error - user may need to sign in again');
    }

    // In development, could add mock data here if needed
    throw error;
  }
};

/**
 * Get current student profile
 */
export const getCurrentStudentProfile = () => handleApiCall(async function getCurrentStudentProfile() {
  console.log('Getting current student profile...');
  
  return await get({
    apiName: API_NAME,
    path: 'students/me',
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Update student profile with partial updates
 */
export const updateStudentProfile = (profileData) => handleApiCall(async function updateStudentProfile() {
  console.log('Updating student profile with data:', profileData);
  
  // Transform the data to match backend expectations
  const payload = {
    // Basic info (only include if provided)
    ...(profileData.name && { name: profileData.name }),
    ...(profileData.phone && { phone: profileData.phone }),
    
    // Course eligibility fields
    ...(profileData.yearLevel && { yearLevel: profileData.yearLevel }),
    ...(profileData.age !== undefined && { age: profileData.age }),
    ...(profileData.dateOfBirth && { dateOfBirth: profileData.dateOfBirth }),
    ...(profileData.gender && { gender: profileData.gender }),
    ...(profileData.school && { school: profileData.school }),
    
    // Friend preferences (for view-only display)
    ...(profileData.friendPreferences !== undefined && { friendPreferences: profileData.friendPreferences }),
    
    // Customer info (billing details)
    ...(profileData.customerInfo && { customerInfo: profileData.customerInfo }),
    
    // Profile settings
    ...(profileData.profile && { profile: profileData.profile })
  };

  console.log('Profile update payload:', JSON.stringify(payload, null, 2));

  return await put({
    apiName: API_NAME,
    path: 'students/me',
    options: {
      body: payload,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Validate Irish phone number - Updated for your format
 */
export const validateIrishPhone = (phone) => {
  if (!phone) return { isValid: false, message: 'Phone number is required' };

  // Remove all spaces, dashes, brackets, and dots - allow any formatting
  const cleanPhone = phone.replace(/[\s\-\(\)\.]/g, '');

  // Very permissive validation:
  // 1. Starts with 0 (local format) - just needs reasonable length
  // 2. Starts with + (international format)
  // 3. Minimum 7 digits, maximum 15 (ITU standard)

  // Check it's mostly digits (allow + at start)
  const digitsOnly = cleanPhone.replace(/^\+/, '');
  if (!/^\d+$/.test(digitsOnly)) {
    return {
      isValid: false,
      message: 'Phone number should only contain digits and spaces'
    };
  }

  // Check reasonable length (7-15 digits)
  if (digitsOnly.length < 7) {
    return {
      isValid: false,
      message: 'Phone number is too short (minimum 7 digits)'
    };
  }

  if (digitsOnly.length > 15) {
    return {
      isValid: false,
      message: 'Phone number is too long (maximum 15 digits)'
    };
  }

  // Accept any reasonable phone number
  return { isValid: true, message: 'Valid phone number' };
};

/**
 * Validate Irish address
 */
export const validateIrishAddress = (address) => {
  const errors = {};
  
  if (!address.billingCity || address.billingCity.trim().length < 2) {
    errors.billingCity = 'City is required';
  }
  
  if (!address.billingAddress1 || address.billingAddress1.trim().length < 5) {
    errors.billingAddress1 = 'Address line 1 is required (minimum 5 characters)';
  }
  
  // Optional postcode validation for Ireland
  if (address.billingPostcode) {
    const cleanPostcode = address.billingPostcode.replace(/\s/g, '').toUpperCase();
    // Basic Irish Eircode pattern: A00 A000 or A00A000
    const irishPattern = /^[ACDEFHKNPRTUVWXY][0-9]{2}[ACDEFHKNPRTUVWXY][0-9A-Z]{3}$/;
    // UK postcode patterns
    const ukPattern = /^[A-Z]{1,2}[0-9][0-9A-Z]?\s?[0-9][A-Z]{2}$/i;
    
    if (!irishPattern.test(cleanPostcode) && !ukPattern.test(cleanPostcode)) {
      errors.billingPostcode = 'Please enter a valid Irish Eircode or UK postcode';
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Clean and format Irish phone number for display
 */
export const formatIrishPhone = (phone) => {
  if (!phone) return '';
  
  const cleanPhone = phone.replace(/[\s-]/g, '');
  
  // Handle various formats and clean up duplications
  let workingNumber = cleanPhone;
  
  // Remove duplicate country codes
  if (workingNumber.startsWith('+353353')) {
    // This is your case: +353353868091210
    workingNumber = '+353' + workingNumber.substring(7); // Remove the duplicate 353
  } else if (workingNumber.startsWith('00353353')) {
    workingNumber = '+353' + workingNumber.substring(8);
  } else if (workingNumber.startsWith('353353')) {
    workingNumber = '+353' + workingNumber.substring(6);
  }
  
  // Convert to +353 format if needed
  if (workingNumber.startsWith('0') && workingNumber.length >= 10) {
    workingNumber = `+353${workingNumber.substring(1)}`;
  } else if (workingNumber.startsWith('353') && workingNumber.length >= 12) {
    workingNumber = `+${workingNumber}`;
  } else if (!workingNumber.startsWith('+353') && workingNumber.length >= 9) {
    // Assume it's an Irish number without country code
    workingNumber = `+353${workingNumber}`;
  }
  
  // Format for display: +353 87 123 4567
  if (workingNumber.startsWith('+353') && workingNumber.length >= 12) {
    const number = workingNumber.substring(4); // Remove +353
    if (number.length >= 9) {
      return `+353 ${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5)}`;
    }
  }
  
  // Return as-is if we can't format it properly
  return workingNumber;
};

/**
 * Clean phone number for storage (removes formatting, fixes duplications)
 */
export const cleanPhoneForStorage = (phone) => {
  if (!phone) return '';
  
  const cleanPhone = phone.replace(/[\s-]/g, '');
  
  // Handle duplicate country codes
  if (cleanPhone.startsWith('+353353')) {
    return '+353' + cleanPhone.substring(7);
  } else if (cleanPhone.startsWith('00353353')) {
    return '+353' + cleanPhone.substring(8);
  } else if (cleanPhone.startsWith('353353')) {
    return '+353' + cleanPhone.substring(6);
  }
  
  // Convert to standard +353 format
  if (cleanPhone.startsWith('0') && cleanPhone.length >= 10) {
    return `+353${cleanPhone.substring(1)}`;
  } else if (cleanPhone.startsWith('353') && cleanPhone.length >= 12) {
    return `+${cleanPhone}`;
  } else if (!cleanPhone.startsWith('+') && cleanPhone.length >= 9) {
    return `+353${cleanPhone}`;
  }
  
  return cleanPhone;
};

/**
 * Get available timezones (common ones for Ireland/UK)
 */
export const getAvailableTimezones = () => [
  { value: 'Europe/Dublin', label: 'Dublin (GMT/IST)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' }
];

/**
 * Get available languages
 */
export const getAvailableLanguages = () => [
  { value: 'en', label: 'English' },
  { value: 'ga', label: 'Gaeilge (Irish)' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'it', label: 'Italiano' },
  { value: 'pt', label: 'Português' }
];

/**
 * Update billing address for student profile
 */
export const updateBillingAddress = (billingData) => handleApiCall(async function updateBillingAddress() {
  console.log('Updating student billing address with data:', billingData);
  
  const payload = {
    customerInfo: {
      billingAddress1: billingData.billingAddress1,
      billingAddress2: billingData.billingAddress2 || '',
      billingCity: billingData.billingCity,
      billingPostcode: billingData.billingPostcode,
      billingCountry: billingData.billingCountry,
      billingCounty: billingData.billingCounty || ''
    }
  };

  console.log('Billing address update payload:', JSON.stringify(payload, null, 2));

  return await put({
    apiName: API_NAME,
    path: 'students/me',
    options: {
      body: payload,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Sync user attributes from Cognito to DynamoDB
 * This is useful when Cognito has attributes that need to be stored in DynamoDB
 */
export const syncUserAttributesToDynamoDB = async (cognitoAttributes) => {
  try {
    // Extract gender and userType from Cognito attributes
    const updates = {
      profile: {}
    };
    
    if (cognitoAttributes['custom:gender']) {
      updates.profile.gender = cognitoAttributes['custom:gender'];
    }
    
    if (cognitoAttributes['custom:user_type']) {
      updates.profile.userType = cognitoAttributes['custom:user_type'];
    }
    
    // Only update if we have something to sync
    if (Object.keys(updates.profile).length > 0) {
      const response = await updateStudentProfile(updates);
      console.log('Synced user attributes to DynamoDB:', response);
      return response;
    }
    
    return null;
  } catch (error) {
    console.error('Error syncing user attributes to DynamoDB:', error);
    // Don't throw - this is a non-critical sync operation
    return null;
  }
};

// Export all functions
const studentService = {
  getCurrentStudentProfile,
  updateStudentProfile,
  updateBillingAddress,
  validateIrishPhone,
  syncUserAttributesToDynamoDB,
  validateIrishAddress,
  formatIrishPhone,
  getAvailableTimezones,
  getAvailableLanguages
};

export default studentService;